import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpenIcon, HeartIcon, FlameIcon, HeartHandshakeIcon, UsersIcon, ListIcon, AlertCircleIcon, MapPinIcon, UserIcon, LinkIcon, ZapIcon, BookmarkIcon, GlobeIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, NavItem } from '../components/UI/FilterSidebar';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { ContentAccordionSection, sortEntriesAlphabetically } from '../components/UI/ContentAccordion';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useDarkMode } from '../context/DarkModeContext';
// Mock data for themes tags with subgenres
const themesTags = [{
  id: 'coming-of-age',
  name: 'Coming of Age',
  description: 'Stories that focus on the psychological and moral growth of a protagonist from youth to adulthood.',
  count: 215,
  category: 'Personal Growth',
  subgenres: ['Contemporary', 'YA', 'Literary Fiction', 'Historical']
}, {
  id: 'redemption',
  name: 'Redemption',
  description: 'Stories where characters seek atonement or redemption for past actions.',
  count: 178,
  category: 'Personal Growth',
  subgenres: ['Fantasy', 'Thriller', 'Romance', 'Sci-Fi']
}, {
  id: 'self-discovery',
  name: 'Self-Discovery',
  description: 'Stories where characters embark on journeys of self-discovery and personal identity.',
  count: 192,
  category: 'Personal Growth',
  subgenres: ['Memoir', 'Contemporary', 'Travel', 'Spiritual']
}, {
  id: 'overcoming-adversity',
  name: 'Overcoming Adversity',
  description: 'Stories where characters face and overcome significant challenges or obstacles.',
  count: 225,
  category: 'Personal Growth',
  subgenres: ['Biography', 'Drama', 'Sports', 'Historical']
}, {
  id: 'chosen-one',
  name: 'Chosen One',
  description: 'Stories featuring a protagonist who is chosen by fate or prophecy for a special purpose.',
  count: 145,
  category: 'Destiny',
  subgenres: ['High Fantasy', 'Urban Fantasy', 'Sci-Fi', 'Dystopian']
}, {
  id: 'fate-vs-free-will',
  name: 'Fate vs. Free Will',
  description: 'Stories exploring the tension between predetermined destiny and personal choice.',
  count: 132,
  category: 'Destiny',
  subgenres: ['Greek Mythology', 'Fantasy', 'Philosophy', 'Sci-Fi']
}, {
  id: 'power-corruption',
  name: 'Power and Corruption',
  description: 'Stories examining how power can corrupt individuals or institutions.',
  count: 156,
  category: 'Power',
  subgenres: ['Political Thriller', 'Dystopian', 'Historical', 'Fantasy']
}, {
  id: 'rebellion',
  name: 'Rebellion',
  description: 'Stories about characters or groups rising up against oppressive systems or authorities.',
  count: 168,
  category: 'Power',
  subgenres: ['Dystopian', 'Sci-Fi', 'Historical', 'Punk']
}, {
  id: 'good-vs-evil',
  name: 'Good vs. Evil',
  description: 'Stories with clear moral distinctions between forces of good and evil.',
  count: 198,
  category: 'Morality',
  subgenres: ['Epic Fantasy', 'Superhero', 'Horror', 'Western']
}, {
  id: 'moral-ambiguity',
  name: 'Moral Ambiguity',
  description: 'Stories exploring complex moral questions without clear right or wrong answers.',
  count: 175,
  category: 'Morality',
  subgenres: ['Noir', 'Grimdark', 'Psychological Thriller', 'Literary Fiction']
}, {
  id: 'sacrifice',
  name: 'Sacrifice',
  description: 'Stories where characters must sacrifice something important for a greater good.',
  count: 167,
  category: 'Morality',
  subgenres: ['War', 'Fantasy', 'Drama', 'Romance']
}, {
  id: 'family',
  name: 'Family',
  description: 'Stories centered on family relationships, dynamics, and bonds.',
  count: 210,
  category: 'Relationships',
  subgenres: ['Family Saga', 'Contemporary', 'Drama', 'Historical']
}, {
  id: 'friendship',
  name: 'Friendship',
  description: 'Stories focused on the power and importance of friendship.',
  count: 195,
  category: 'Relationships',
  subgenres: ['Middle Grade', 'YA', 'Contemporary', 'Fantasy']
}, {
  id: 'betrayal',
  name: 'Betrayal',
  description: 'Stories featuring significant acts of betrayal and their consequences.',
  count: 158,
  category: 'Relationships',
  subgenres: ['Thriller', 'Mystery', 'Romance', 'Historical']
}, {
  id: 'isolation',
  name: 'Isolation',
  description: 'Stories exploring themes of loneliness, isolation, or alienation.',
  count: 142,
  category: 'Relationships',
  subgenres: ['Horror', 'Sci-Fi', 'Literary Fiction', 'Survival']
}, {
  id: 'identity',
  name: 'Identity',
  description: 'Stories exploring questions of personal, cultural, or social identity.',
  count: 185,
  category: 'Identity',
  subgenres: ['LGBTQ+', 'Immigrant Experience', 'Coming of Age', 'Memoir']
}, {
  id: 'prejudice',
  name: 'Prejudice',
  description: 'Stories examining bias, discrimination, and prejudice in society.',
  count: 165,
  category: 'Social Issues',
  subgenres: ['Historical', 'Social Justice', 'Contemporary', 'Dystopian']
}];
const Themes: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || 'all');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const dk = useDarkClasses();
  const {
    darkMode
  } = useDarkMode();
  // Helper function to get color based on theme category
  const getThemeColor = (category: string) => {
    if (darkMode) {
      switch (category) {
        case 'Personal Growth':
          return 'bg-blue-900/30 text-blue-300';
        case 'Destiny':
          return 'bg-indigo-900/30 text-indigo-300';
        case 'Power':
          return 'bg-red-900/30 text-red-300';
        case 'Morality':
          return 'bg-green-900/30 text-green-300';
        case 'Relationships':
          return 'bg-pink-900/30 text-pink-300';
        case 'Identity':
          return 'bg-indigo-900/30 text-indigo-300';
        case 'Social Issues':
          return 'bg-amber-900/30 text-amber-300';
        default:
          return dk.badgeNeutral;
      }
    }
    switch (category) {
      case 'Personal Growth':
        return 'bg-blue-100 text-blue-900';
      case 'Destiny':
        return 'bg-indigo-100 text-indigo-900';
      case 'Power':
        return 'bg-red-100 text-red-900';
      case 'Morality':
        return 'bg-green-100 text-green-900';
      case 'Relationships':
        return 'bg-pink-100 text-pink-900';
      case 'Identity':
        return 'bg-indigo-100 text-indigo-900';
      case 'Social Issues':
        return 'bg-amber-100 text-amber-900';
      default:
        return 'bg-gray-100 text-gray-900';
    }
  };
  // Sync selectedCategory from URL params reactively
  useEffect(() => {
    const category = searchParams.get('category');
    setSelectedCategory(category || 'all');
  }, [searchParams]);
  const groupedTags = themesTags.reduce((acc, tag) => {
    const category = tag.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(tag);
    return acc;
  }, {} as Record<string, typeof themesTags>);
  const filteredGroupedTags = Object.entries(groupedTags).reduce((acc, [category, tags]) => {
    if (selectedCategory !== 'all' && category !== selectedCategory) return acc;
    const filteredTags = tags.filter(tag => tag.name.toLowerCase().includes(searchQuery.toLowerCase()) || tag.description.toLowerCase().includes(searchQuery.toLowerCase()));
    if (filteredTags.length > 0) acc[category] = filteredTags;
    return acc;
  }, {} as Record<string, typeof themesTags>);
  const contentNavItems: NavItem[] = [{
    label: 'View All Tags',
    href: '/tagged-books?type=all',
    icon: ListIcon
  }, {
    label: 'Tropes',
    href: '/tropes',
    icon: HeartHandshakeIcon,
    subItems: [{
      label: 'Romance',
      href: '/tropes?category=romance'
    }, {
      label: 'Fantasy',
      href: '/tropes?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/tropes?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/tropes?category=scifi'
    }, {
      label: 'Horror',
      href: '/tropes?category=horror'
    }, {
      label: 'General',
      href: '/tropes?category=general'
    }]
  }, {
    label: 'Characters',
    href: '/characters',
    icon: UserIcon,
    subItems: [{
      label: 'Romance',
      href: '/characters?category=romance'
    }, {
      label: 'Fantasy',
      href: '/characters?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/characters?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/characters?category=scifi'
    }, {
      label: 'Horror',
      href: '/characters?category=horror'
    }, {
      label: 'General',
      href: '/characters?category=general'
    }]
  }, {
    label: 'Relationship Types',
    href: '/relationship-types',
    icon: UsersIcon,
    subItems: [{
      label: 'Romance',
      href: '/relationship-types?category=romance'
    }, {
      label: 'Fantasy',
      href: '/relationship-types?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/relationship-types?category=mystery'
    }, {
      label: 'General',
      href: '/relationship-types?category=general'
    }]
  }, {
    label: 'Pairing Types',
    href: '/pairing-types',
    icon: LinkIcon,
    subItems: [{
      label: 'Monogamous',
      href: '/pairing-types?category=monogamous'
    }, {
      label: 'Why Choose',
      href: '/pairing-types?category=why-choose'
    }, {
      label: 'Closed Triad',
      href: '/pairing-types?category=closed-triad'
    }, {
      label: 'Polyamorous',
      href: '/pairing-types?category=polyamorous'
    }, {
      label: 'NB / Trans Inclusive',
      href: '/pairing-types?category=nb-trans'
    }, {
      label: 'No Romance',
      href: '/pairing-types?category=no-romance'
    }]
  }, {
    label: 'Spice & Darkness',
    href: '/spice-darkness',
    icon: FlameIcon,
    subItems: [{
      label: 'Spice Levels',
      href: '/spice-darkness#spice'
    }, {
      label: 'Darkness Levels',
      href: '/spice-darkness#darkness'
    }]
  }, {
    label: 'Burn Levels',
    href: '/burn-levels',
    icon: ZapIcon
  }, {
    label: 'Ending Types',
    href: '/ending-types',
    icon: BookmarkIcon
  }, {
    label: 'Representation',
    href: '/representation',
    icon: UsersIcon,
    subItems: [{
      label: 'Sexual Orientation & Gender',
      href: '/representation?category=Sexual+Orientation+%26+Gender'
    }, {
      label: 'Race & Ethnicity',
      href: '/representation?category=Race+%26+Ethnicity'
    }, {
      label: 'Neurodiversity & Disability',
      href: '/representation?category=Neurodiversity+%26+Disability'
    }, {
      label: 'Mental Health',
      href: '/representation?category=Mental+Health'
    }]
  }, {
    label: 'Cultural Identity',
    href: '/cultural-identity',
    icon: GlobeIcon
  }, {
    label: 'Themes',
    href: '/themes',
    icon: BookOpenIcon,
    isActive: true,
    subItems: [{
      label: 'Personal Growth',
      href: '/themes?category=Personal+Growth'
    }, {
      label: 'Destiny',
      href: '/themes?category=Destiny'
    }, {
      label: 'Power',
      href: '/themes?category=Power'
    }, {
      label: 'Morality',
      href: '/themes?category=Morality'
    }, {
      label: 'Relationships',
      href: '/themes?category=Relationships'
    }, {
      label: 'Identity',
      href: '/themes?category=Identity'
    }, {
      label: 'Social Issues',
      href: '/themes?category=Social+Issues'
    }]
  }, {
    label: 'Settings',
    href: '/settings',
    icon: MapPinIcon,
    subItems: [{
      label: 'Contemporary',
      href: '/settings?category=Contemporary'
    }, {
      label: 'Historical',
      href: '/settings?category=Historical'
    }, {
      label: 'Fantasy',
      href: '/settings?category=Fantasy'
    }, {
      label: 'Science Fiction',
      href: '/settings?category=Science%20Fiction'
    }]
  }, {
    label: 'Content Warnings',
    href: '/content-warnings',
    icon: AlertCircleIcon
  }];
  return <div className={dk.page} data-id="element-15679" data-mp="16uq4gw23r4b9">
      <ContentPageHeader title="Themes" icon={getContentPageIcon('themes')} searchPlaceholder="Search themes..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={sidebarOpen} setShowFilters={setSidebarOpen} data-id="element-15680" data-mp="16tyk6hdygt0a" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-15681" data-mp="1evxoec8aksjt">
        <div className="flex gap-8" data-id="element-15682" data-mp="10vwnwxvxvn4v">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-15683" data-mp="10uv5g8clfyd4">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={[]} onReset={() => setSearchQuery('')} searchable={false} data-id="element-15684" data-mp="r4a62ip3ipt5" />
          </FilterSidebarWrapper>
          <main className="flex-1 min-w-0" data-id="element-15685" data-mp="m7s4tssz59lk">
            {Object.keys(filteredGroupedTags).length === 0 ? <div className="text-center py-12" data-id="element-15686" data-mp="87r6ak06me99">
                <p className={dk.textMuted} data-id="element-15687" data-mp="d62dso308cob">
                  No themes found matching your search.
                </p>
              </div> : searchQuery ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-15688" data-mp="3ps0u6mo84ui3">
                {Object.values(filteredGroupedTags).flat().map(tag => <Link key={tag.id} to={`/tagged-books/${tag.id}`} className={`${getThemeColor(tag.category)} rounded-lg p-3 hover:shadow-md transition-shadow`} data-id="element-15689" data-mp="2ivpureooy83">
                      <div className="flex justify-between items-center mb-1" data-id="element-15690" data-mp="7ja1ejfhuqav">
                        <div className="font-medium" data-id="element-15691" data-mp="m9pl1ts9hq6x">{tag.name}</div>
                        <div className="text-xs opacity-80" data-id="element-15692" data-mp="mb0ofshsovxi">
                          {tag.count} books
                        </div>
                      </div>
                      {tag.description && <p className="text-xs mt-1 opacity-90" data-id="element-15693" data-mp="qat38mxs7ybb">
                          {tag.description}
                        </p>}
                    </Link>)}
              </div> : <div className="space-y-3" data-id="element-15694" data-mp="3p2x7cpp1szgh">
                {sortEntriesAlphabetically(Object.entries(filteredGroupedTags)).map(([category, tags]) => <ContentAccordionSection key={category} title={category} itemCount={tags.length} data-id="element-15695" data-mp="1p1jrqoh8kdf">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-15696" data-mp="bmpkcx8rkm3i">
                      {tags.map(tag => <Link key={tag.id} to={`/tagged-books/${tag.id}`} className={`${getThemeColor(tag.category)} rounded-lg p-3 hover:shadow-md transition-shadow`} data-id="element-15697" data-mp="gndi01ri016p">
                          <div className="flex justify-between items-center mb-1" data-id="element-15698" data-mp="2me8u6jsd6ln4">
                            <div className="font-medium" data-id="element-15699" data-mp="2ev800p3ge4cf">{tag.name}</div>
                            <div className="text-xs opacity-80" data-id="element-15700" data-mp="2exkm9eg8v0va">
                              {tag.count} books
                            </div>
                          </div>
                          {tag.description && <p className="text-xs mt-1 opacity-90" data-id="element-15701" data-mp="214cunms5y5dl">
                              {tag.description}
                            </p>}
                        </Link>)}
                    </div>
                  </ContentAccordionSection>)}
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default Themes;